import React, { useState, useEffect } from 'react';
import { Users, BookOpen, Activity, Plus, Trash2, AlertCircle } from 'lucide-react';
import { getTerminology, saveTerminology, deleteTerminology, getUsers, getLogs, updateUserRole, deleteUser } from '../services/store';
import { TerminologyItem, Language, User, AuditLog } from '../types';
import { LANGUAGE_LABELS } from '../constants';
import { supabase } from '../services/supabaseClient';

const Admin: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'users' | 'logs' | 'terms'>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [terms, setTerms] = useState<TerminologyItem[]>([]);
  const [refresh, setRefresh] = useState(0);

  // Terminology Form State
  const [showTermForm, setShowTermForm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [newTerm, setNewTerm] = useState<Partial<TerminologyItem>>({
    language: Language.DARI,
    type: 'forbidden'
  });

  useEffect(() => {
    const fetchData = async () => {
      const [u, l, t] = await Promise.all([getUsers(), getLogs(), getTerminology()]);
      setUsers(u);
      setLogs(l);
      setTerms(t);
    };
    fetchData();
  }, [refresh, activeTab]);

  // --- User Management Handlers ---

  const handleRoleChange = async (userId: string, userName: string, newRole: string) => {
    if (confirm(`Are you sure you want to change ${userName}'s role to ${newRole}?`)) {
      try {
        await updateUserRole(userId, newRole);
        setRefresh(prev => prev + 1);
      } catch (e: any) {
        alert("Failed to update role: " + e.message);
      }
    }
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (confirm(`Are you sure you want to suspend/delete ${userName}? They will no longer be able to access the system.`)) {
      try {
        await deleteUser(userId);
        setRefresh(prev => prev + 1);
      } catch (e: any) {
        alert("Failed to suspend user: " + e.message);
      }
    }
  };

  // --- Terminology Handlers ---

  const handleSaveTerm = async () => {
    setFormError(null);
    if (!newTerm.original || !newTerm.replacement || !newTerm.language) {
      setFormError("Original term, Replacement term, and Language are required.");
      return;
    }
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Must be logged in to save terms.");

      await saveTerminology({
        id: crypto.randomUUID(),
        original: newTerm.original,
        replacement: newTerm.replacement,
        language: newTerm.language,
        type: newTerm.type as 'forbidden' | 'standard',
        context: newTerm.context,
        // createdBy and createdAt are handled in the store/db
      }, user.id);
      
      setRefresh(prev => prev + 1);
      setShowTermForm(false);
      setNewTerm({ language: Language.DARI, type: 'forbidden' });
    } catch (e: any) {
      setFormError(e.message);
    }
  };

  const handleDeleteTerm = async (id: string) => {
    if (confirm("Delete this terminology rule?")) {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await deleteTerminology(id, user.id);
        setRefresh(prev => prev + 1);
      }
    }
  };

  const getUserName = (userId?: string) => {
    if (!userId) return 'System';
    const user = users.find(u => u.id === userId);
    return user ? user.name : 'Unknown User';
  };

  return (
    <div className="p-8 max-w-7xl mx-auto h-full overflow-y-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Admin Dashboard</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Manage users, view activity, and configure terminology.</p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl mb-6 w-fit">
        <button
          onClick={() => setActiveTab('users')}
          className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'users' ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900'}`}
        >
          <Users className="h-4 w-4 mr-2" /> Users
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'logs' ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900'}`}
        >
          <Activity className="h-4 w-4 mr-2" /> Activity Logs
        </button>
        <button
          onClick={() => setActiveTab('terms')}
          className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'terms' ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900'}`}
        >
          <BookOpen className="h-4 w-4 mr-2" /> Terminology DB
        </button>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden min-h-[400px]">
        {/* USERS TAB */}
        {activeTab === 'users' && (
          <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
            <thead className="bg-slate-50 dark:bg-slate-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">User</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Role</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Last Login</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {users.map(user => {
                const isSuspended = user.role === 'suspended';
                return (
                  <tr key={user.id} className={isSuspended ? "bg-slate-50 dark:bg-slate-900/50 opacity-60" : ""}>
                    <td className="px-6 py-4 flex items-center">
                      <img src={user.avatar || `https://ui-avatars.com/api/?name=${user.name}`} className="h-8 w-8 rounded-full mr-3" alt="" />
                      <span className={`text-sm font-medium ${isSuspended ? 'text-slate-500 line-through' : 'text-slate-900 dark:text-slate-100'}`}>{user.name}</span>
                      {isSuspended && <span className="ml-2 text-xs text-red-500 font-bold">(Suspended)</span>}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500">
                       <select
                          value={user.role}
                          onChange={(e) => handleRoleChange(user.id, user.name, e.target.value)}
                          className={`bg-slate-100 dark:bg-slate-800 border-none rounded text-xs uppercase font-bold tracking-wider py-1 px-2 cursor-pointer focus:ring-2 focus:ring-blue-500 ${
                            isSuspended ? 'text-red-600' : 'text-slate-700 dark:text-slate-300'
                          }`}
                        >
                          <option value="journalist">Journalist</option>
                          <option value="editor">Editor</option>
                          <option value="admin">Admin</option>
                          <option value="suspended">Suspended</option>
                        </select>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500">{user.email}</td>
                    <td className="px-6 py-4 text-sm text-slate-500">{user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}</td>
                    <td className="px-6 py-4 text-right">
                       {!isSuspended && (
                         <button 
                           onClick={() => handleDeleteUser(user.id, user.name)}
                           className="p-2 text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors"
                           title="Suspend User"
                         >
                           <Trash2 className="h-4 w-4" />
                         </button>
                       )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* LOGS TAB */}
        {activeTab === 'logs' && (
          <div className="flex flex-col">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                <thead className="bg-slate-50 dark:bg-slate-800/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Time</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Action</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {logs.map(log => (
                    <tr key={log.id}>
                      <td className="px-6 py-3 text-sm text-slate-500 whitespace-nowrap">{new Date(log.timestamp).toLocaleString()}</td>
                      <td className="px-6 py-3 text-sm font-medium text-slate-900 dark:text-slate-100">{log.userName}</td>
                      <td className="px-6 py-3 text-sm text-blue-600 dark:text-blue-400">{log.action}</td>
                      <td className="px-6 py-3 text-sm text-slate-500 truncate max-w-md">{log.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TERMINOLOGY TAB */}
        {activeTab === 'terms' && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-900/30 flex-1 mr-6">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100 text-sm">How this works</h3>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                  Terms defined here are strictly enforced by the AI. Duplicate terms for the same language are prevented.
                </p>
              </div>
              <button 
                onClick={() => {
                  setFormError(null);
                  setShowTermForm(true);
                }}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-medium"
              >
                <Plus className="h-4 w-4 mr-2" /> Add Term
              </button>
            </div>

            {showTermForm && (
              <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg border border-slate-200 dark:border-slate-700 mb-6 animate-in fade-in slide-in-from-top-4">
                {formError && (
                  <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 p-3 rounded-md flex items-center text-sm">
                    <AlertCircle className="h-4 w-4 mr-2" /> {formError}
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Incorrect / Original Term</label>
                    <input 
                      type="text" 
                      value={newTerm.original || ''}
                      onChange={e => setNewTerm({...newTerm, original: e.target.value})}
                      className="w-full rounded-md border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm"
                      placeholder="e.g. Daneshgah"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Correct Replacement</label>
                    <input 
                      type="text" 
                      value={newTerm.replacement || ''}
                      onChange={e => setNewTerm({...newTerm, replacement: e.target.value})}
                      className="w-full rounded-md border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm"
                      placeholder="e.g. Pohantoon"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Language</label>
                    <select
                       value={newTerm.language}
                       onChange={e => setNewTerm({...newTerm, language: e.target.value as Language})}
                       className="w-full rounded-md border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm"
                    >
                      {Object.entries(LANGUAGE_LABELS).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Context / Notes</label>
                    <input 
                      type="text" 
                      value={newTerm.context || ''}
                      onChange={e => setNewTerm({...newTerm, context: e.target.value})}
                      className="w-full rounded-md border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm"
                      placeholder="e.g. Use Afghan terminology for University"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button onClick={() => setShowTermForm(false)} className="text-sm text-slate-500 hover:text-slate-900 px-3 py-2">Cancel</button>
                  <button onClick={handleSaveTerm} className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700">Save Rule</button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                <thead className="bg-slate-50 dark:bg-slate-800/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Original</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Replacement</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Language</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Created By</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Context</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {terms.map(term => (
                    <tr key={term.id} className="group hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="px-6 py-4 text-sm text-red-500 line-through decoration-red-500/50">{term.original}</td>
                      <td className="px-6 py-4 text-sm font-bold text-green-600 dark:text-green-400">{term.replacement}</td>
                      <td className="px-6 py-4 text-sm text-slate-500">{LANGUAGE_LABELS[term.language]}</td>
                      <td className="px-6 py-4 text-sm text-slate-500">
                        <div className="font-medium text-slate-700 dark:text-slate-300">{getUserName(term.createdBy)}</div>
                        <div className="text-xs text-slate-400">{term.createdAt ? new Date(term.createdAt).toLocaleDateString() : '-'}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500 italic">{term.context}</td>
                      <td className="px-6 py-4 text-right">
                        <button 
                          onClick={() => handleDeleteTerm(term.id)}
                          className="text-slate-300 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {terms.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-10 text-center text-slate-400 italic">No terminology rules defined yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;